import { getCookie, setCookie } from './cookie-handler';
import { blindPostRequest } from './network';
import siteConfig from '../data/config/site-config.json';

/** Properties sent alongside a posthog event. */
type CaptureProperties = Record<string, unknown>;

/** A tracked param is null until we have seen a value for it. */
type TrackedParams = Record<string, string | null>;

declare global {
  interface Window {
    /**
     * Posthog script lives (or must live) in the head of html document.
     * This should mean that it is accessible via window object. It is absent
     * until the visitor consents to statistics, so always guard access.
     */
    posthog?: {
      capture: (event: string, properties?: CaptureProperties) => void;
    };
    DISQUS?: any;
    Trustpilot?: {
      loadFromElement: (element: HTMLElement | null, flag: boolean) => void;
    };
  }
}

const COOKIE_EXPIRY_DAYS = 360 as const;
const POSTHOG_REQUEST_MILLIS = 1000 as const;

/**
 * Data we want to track
 */
const paramsToTrack = siteConfig.analytics_pks.reduce<TrackedParams>(
  (acc, curr) => {
    return (acc[curr] = null), acc;
  },
  {}
);

/**
 * Split tracked params by consent category.
 * Ad-click identifiers are Marketing; the rest (campaign params) are Statistics.
 *
 * `analytics_pks` is edited in Tina, so new params are picked up automatically -
 * but a new ad-click param (another `*clid`) has to be added here too, or it
 * lands in the Statistics bucket instead of Marketing.
 */
const MARKETING_PARAMS = [
  'gclid',
  'fbclid',
  'li_fat_id',
  'twclid',
  'msclkid',
  'rdt_cid',
];
const STATISTICS_PARAMS = siteConfig.analytics_pks.filter(
  p => !MARKETING_PARAMS.includes(p)
);

/**
 * Get the value of a URL parameter
 * @param name URL parameter to check
 * @returns parameter string value or empty string
 */
const getParam = (name: string): string => {
  if (typeof window !== 'undefined') {
    const match = RegExp('[?&]' + name + '=([^&]*)').exec(
      window.location.search
    );
    const theParam = match?.[1];
    return theParam ? decodeURIComponent(theParam.replace(/\+/g, ' ')) : '';
  }
  return '';
};

/**
 * Check if parameter value is valid
 * @param value parameter to check
 * @returns boolean
 */
const isUrlParamValid = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const TRACKED_PARAMS = Array.from(
  new Set([...STATISTICS_PARAMS, ...MARKETING_PARAMS])
);

/**
 * What the visitor arrived with, held in memory only. Nothing here is stored
 * until the matching category is granted, so someone who lands on a campaign
 * URL, browses on, and only then accepts still keeps their attribution instead
 * of losing it with the URL.
 */
const landingParams: Record<string, string> = {};
let landingReferrer: string | null = null;

const rememberLanding = () => {
  if (typeof window === 'undefined') return;

  TRACKED_PARAMS.forEach(name => {
    const value = getParam(name);
    // Later routes rarely carry params; when one does it wins, as before.
    if (isUrlParamValid(value)) {
      landingParams[name] = value;
    }
  });

  // Only the first referrer is the real one - after that it is our own page.
  if (landingReferrer === null) {
    landingReferrer = `${document.referrer}`;
  }
};

/**
 * Capture the given params to cookies + the in-memory tracking object, taking
 * what the visitor landed with in preference to the current URL. If neither has
 * it, fall back to any existing cookie value.
 */
const captureParams = (names: string[]) =>
  new Promise<void>(resolve => {
    names.forEach(name => {
      let value = landingParams[name] || getParam(name);
      if (isUrlParamValid(value)) {
        setCookie(name, value, COOKIE_EXPIRY_DAYS);
      } else {
        value = getCookie(name);
      }
      if (isUrlParamValid(value)) {
        paramsToTrack[name] = value;
      }
    });
    resolve();
  });

// Statistics: referrer domain for attribution
const setRefCookie = () =>
  new Promise<void>(resolve => {
    const referrer = landingReferrer ?? `${document.referrer}`;
    if (!referrer.includes('bunny.net/')) {
      setCookie('ref_domain', referrer, COOKIE_EXPIRY_DAYS);
    }
    resolve();
  });

// Marketing CATEGORY — but DEFERRED by decision: this is called UNGATED from
// gatsby-browser.js (via trackAffiliate), so it still fires pre-consent. Not
// wired into the Marketing-gated path.
const setAffiliateCookies = () =>
  new Promise<void>(resolve => {
    const ref = getParam('ref');
    if (ref !== null && isUrlParamValid(ref)) {
      fetch(
        'https://api.bunny.net/system/register-affiliate-click?refCode=' +
          encodeURIComponent(ref)
      );
      setCookie('AffiliateCode', ref, 30);
    }
    resolve();
  });

/**
 * Send tracking data to posthog
 * @returns a Promise
 */
const trackCampaignInfo = () =>
  new Promise<void>((resolve, reject) => {
    // capture as campaign event
    if (paramsToTrack['pk_campaign'] !== null) {
      /**
       * Properties that we want to capture in posthog
       * Consistently save them with a pk_ prefix.
       */
      const captureProperties = Object.entries(
        paramsToTrack
      ).reduce<TrackedParams>((acc, [key, value]) => {
        const keyWithPKPrefix = key.startsWith('pk') ? key : `pk_${key}`;
        return (acc[keyWithPKPrefix] = value), acc;
      }, {});
      /**
       * Filter out defined (not null) capture properties
       */
      const definedCaptureProperties: CaptureProperties = {};
      Object.entries(captureProperties).forEach(([key, value]) => {
        if (value !== null) definedCaptureProperties[key] = value;
      });
      /**
       * Capture a posthog event
       */
      PostHogHelper.capture('Campaign Info', definedCaptureProperties);
      /**
       * Save data to user instance
       */
      PostHogHelper.capture('Save User Campaign Info', {
        $set_once: definedCaptureProperties,
      });
    }

    resolve();
  });

const PostHogHelper = {
  // Memory only, writes nothing - safe to call before the visitor answers.
  rememberLanding,
  // Runs only after STATISTICS consent (wired in gatsby-browser.js)
  analyseStatistics: async () => {
    PostHogHelper.capture('$pageview');
    await captureParams(STATISTICS_PARAMS);
    await setRefCookie();
    await trackCampaignInfo();
  },
  // Runs only after MARKETING consent (wired in gatsby-browser.js)
  analyseMarketing: async () => {
    await captureParams(MARKETING_PARAMS);
  },
  // DEFERRED (accepted risk): affiliate attribution still fires pre-consent.
  // Called UNGATED from gatsby-browser.js. To finish the job later, delete this
  // and move setAffiliateCookies() back into analyseMarketing().
  trackAffiliate: async () => {
    await setAffiliateCookies();
  },
  captureEmail: (email: string) => {
    PostHogHelper.capture('Identify by Form', {
      $set: {
        email,
      },
    });
  },
  capture: (event: string, properties?: CaptureProperties) => {
    if (typeof window !== 'undefined' && window.posthog) {
      if (properties) {
        window.posthog.capture(event, properties);
      } else {
        window.posthog.capture(event);
      }
    }
  },
  promiseToCaptureEmail: async (email: string): Promise<void> => {
    // This is what we want to do ...
    PostHogHelper.captureEmail(email);

    return new Promise(resolve => {
      /**
       * There's an open issue about posthog.capture() not being a promise:
       * https://github.com/PostHog/posthog-js/issues/205
       *
       * We will fake a timeout in case a page redirect is about to happen.
       * Hopefully the capture event will happen faster than in 1 second.
       */
      setTimeout(() => {
        resolve();
      }, POSTHOG_REQUEST_MILLIS);
    });
  },
  /**
   * With enough testing abilities and confidence,
   * promiseToCaptureEmail should be replaced with requestToCapture
   */
  requestToCapture: async (properties: CaptureProperties, event: string) => {
    const apiHost = process.env.GATSBY_POSTHOG_API_HOST;
    if (!apiHost) {
      return undefined;
    }

    // Will send a network request to capture posthog event
    const timestamp = Date.now();
    return blindPostRequest(apiHost, {
      api_key: process.env.GATSBY_POSTHOG_API_KEY,
      properties,
      timestamp,
      event,
    });
  },

  requestToCaptureEmail: async (email: string) => {
    return PostHogHelper.requestToCapture(
      {
        email,
      },
      'Identify by Form'
    );
  },
};

export const constructPostHogScriptBody = () => `
!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
posthog.init('${process.env.GATSBY_POSTHOG_API_KEY}', ${JSON.stringify({
  api_host: process.env.GATSBY_POSTHOG_API_HOST,
})})
`;

export default PostHogHelper;
